import React, { useState, useEffect } from 'react';
import {
  Paper,
  Button,
  Box,
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tabs,
  Tab,
  Tooltip,
  IconButton,
  Divider,
  Grid,
  Alert,
} from '@mui/material';
import { Copy, Check, ExternalLink } from 'react-feather';
import { enqueueSnackbar } from 'notistack';
import {
  DataGridPro,
  GridColDef,
  GridRenderCellParams,
  GridRowParams,
} from '@mui/x-data-grid-pro';
import { useQuery, useMutation } from '@apollo/client';
import moment from 'moment';
import copy from 'copy-to-clipboard';
import { FullScreen } from '@pv/common/components';
import { offsetToTimeString } from '@pv/common/utils';
import { appHost } from '@pv/common/api';

import {
  ADMIN_OPENTABLE_SYNCS_QUERY,
  ADMIN_MARK_OPENTABLE_SYNC_COMPLETE_MUTATION,
} from './graphql';
import { reportSx } from './styles';
const APP_HOST = appHost(
  process.env.REACT_APP_PV_ENV || 'app.perfectvenue.com'
);
interface OpentableSync {
  id: string;
  status: string;
  externalId: string | null;
  lastSyncedAt: string | null;
  errorMessage: string | null;
  createdAt: string;
  updatedAt: string;
  eventId: string;
  eventName: string | null;
  eventStatus: string | null;
  startDate: string | null;
  endDate: string | null;
  startOffset: number | null;
  endOffset: number | null;
  groupSize: number | null;
  guestName: string | null;
  guestEmail: string | null;
  guestPhone: string | null;
  venueId: string | null;
  venueName: string | null;
  opentableRestaurantId: string | null;
  spaceNames: string | null;
  opentableCustomInstructions: string | null;
}

type StatusFilter = 'all' | 'pending' | 'syncing' | 'failed';

export const OpenTableSyncsTable = () => {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [limitInput, setLimitInput] = useState(100);
  const [limit, setLimit] = useState(100);
  const [selectedSync, setSelectedSync] = useState<OpentableSync | null>(null);
  const [reservationId, setReservationId] = useState('');
  const [detailReservationId, setDetailReservationId] = useState('');
  const [copiedField, setCopiedField] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLimit(limitInput);
    }, 700);
    return () => clearTimeout(timer);
  }, [limitInput]);

  const { data, loading, refetch } = useQuery(ADMIN_OPENTABLE_SYNCS_QUERY, {
    variables: {
      status: statusFilter === 'all' ? undefined : statusFilter,
      first: limit,
      orderBy: 'created_at',
      order: 'asc',
    },
  });

  const [markComplete, { loading: markingComplete }] = useMutation(
    ADMIN_MARK_OPENTABLE_SYNC_COMPLETE_MUTATION,
    {
      onCompleted: (responseData) => {
        if (responseData.adminMarkOpentableSyncComplete.errors.length === 0) {
          enqueueSnackbar('Sync marked as complete', { variant: 'success' });
          refetch();
          handleCloseDialog();
        } else {
          enqueueSnackbar(
            responseData.adminMarkOpentableSyncComplete.errors[0].message,
            { variant: 'error' }
          );
        }
      },
      onError: (error) => {
        enqueueSnackbar(error.message, { variant: 'error' });
      },
    }
  );

  const handleOpenCompleteDialog = (sync: OpentableSync) => {
    setSelectedSync(sync);
    setReservationId(sync.externalId || '');
    setCompleteDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setCompleteDialogOpen(false);
    setSelectedSync(null);
    setReservationId('');
  };

  const handleRowClick = (params: GridRowParams) => {
    const sync = params.row as OpentableSync;
    setSelectedSync(sync);
    setDetailReservationId(sync.externalId || '');
    setDetailDialogOpen(true);
  };

  const handleCloseDetailDialog = () => {
    setDetailDialogOpen(false);
    setSelectedSync(null);
    setDetailReservationId('');
  };

  const handleMarkCompleteFromDetail = () => {
    if (selectedSync) {
      markComplete({
        variables: {
          id: selectedSync.id,
          reservationId: detailReservationId || null,
        },
      });
      handleCloseDetailDialog();
    }
  };

  const handleMarkComplete = () => {
    if (selectedSync) {
      markComplete({
        variables: {
          id: selectedSync.id,
          reservationId: reservationId || null,
        },
      });
    }
  };

  const handleCopy = (text: string, fieldName: string) => {
    copy(text);
    setCopiedField(fieldName);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const formatPhone = (phone: string | null) => {
    if (!phone) return '-';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(
        6
      )}`;
    }
    return phone;
  };

  const columns: GridColDef[] = [
    {
      field: 'status',
      headerName: 'Status',
      width: 100,
      renderCell: (params: GridRenderCellParams) => {
        const getStatusColor = (status: string) => {
          if (status === 'pending') return 'warning';
          if (status === 'syncing') return 'info';
          return 'error';
        };
        return (
          <Chip
            label={params.value}
            size="small"
            color={getStatusColor(params.value)}
          />
        );
      },
    },
    {
      field: 'venueName',
      headerName: 'Venue',
      flex: 1,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams) => (
        <span>
          {params.value} ({params.row.venueId})
        </span>
      ),
    },
    {
      field: 'eventId',
      headerName: 'Event ID',
      width: 100,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <span>{params.value}</span>
          <Tooltip title="Open in Core">
            <IconButton
              size="small"
              href={`${APP_HOST}/events/${params.value}`}
              target="_blank"
              onClick={(e) => e.stopPropagation()}
            >
              <ExternalLink size={14} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
    {
      field: 'eventName',
      headerName: 'Event Name',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'visitNotes',
      headerName: 'Visit Notes',
      flex: 1,
      minWidth: 250,
      renderCell: (params: GridRenderCellParams) => {
        const eventName = params.row.eventName || '';
        const spaceNames = params.row.spaceNames || '';
        const visitNotesText = `Event Name: ${eventName}, Event spaces: ${spaceNames}`;
        const fieldKey = `visitNotes-${params.row.id}`;

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title={visitNotesText}>
              <span
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {visitNotesText}
              </span>
            </Tooltip>
            <Tooltip title={copiedField === fieldKey ? 'Copied!' : 'Copy'}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCopy(visitNotesText, fieldKey);
                }}
              >
                {copiedField === fieldKey ? (
                  <Check size={14} />
                ) : (
                  <Copy size={14} />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      field: 'updatedAt',
      headerName: 'Last Updated',
      width: 110,
      valueFormatter: (params) =>
        params.value ? moment(params.value).format('MM/DD/YYYY h:mm A') : '-',
    },
    {
      field: 'eventStatus',
      headerName: 'Event Status',
      width: 120,
    },
    {
      field: 'startDate',
      headerName: 'Start Date',
      width: 110,
      valueFormatter: (params) =>
        params.value ? moment(params.value).format('MM/DD/YYYY') : '-',
    },
    {
      field: 'endDate',
      headerName: 'End Date',
      width: 110,
      valueFormatter: (params) =>
        params.value ? moment(params.value).format('MM/DD/YYYY') : '-',
    },
    {
      field: 'startOffset',
      headerName: 'Start Time',
      width: 100,
      valueFormatter: (params) =>
        params.value != null ? offsetToTimeString(params.value) : '-',
    },
    {
      field: 'endOffset',
      headerName: 'End Time',
      width: 100,
      valueFormatter: (params) =>
        params.value != null ? offsetToTimeString(params.value) : '-',
    },
    {
      field: 'groupSize',
      headerName: 'Group Size',
      width: 100,
      type: 'number',
    },
    {
      field: 'guestName',
      headerName: 'Guest Name',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'guestEmail',
      headerName: 'Guest Email',
      flex: 1,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams) =>
        params.value ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <span>{params.value}</span>
            <Tooltip
              title={
                copiedField === `email-${params.row.id}` ? 'Copied!' : 'Copy'
              }
            >
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCopy(params.value, `email-${params.row.id}`);
                }}
              >
                {copiedField === `email-${params.row.id}` ? (
                  <Check size={14} />
                ) : (
                  <Copy size={14} />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        ) : (
          '-'
        ),
    },
    {
      field: 'guestPhone',
      headerName: 'Guest Phone',
      width: 140,
      valueFormatter: (params) => formatPhone(params.value),
    },
    {
      field: 'spaceNames',
      headerName: 'Spaces',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'opentableRestaurantId',
      headerName: 'Restaurant ID',
      width: 130,
    },
    {
      field: 'externalId',
      headerName: 'Reservation ID',
      width: 130,
    },
    {
      field: 'errorMessage',
      headerName: 'Error',
      flex: 1,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams) =>
        params.value ? (
          <Tooltip title={params.value}>
            <span
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {params.value}
            </span>
          </Tooltip>
        ) : (
          '-'
        ),
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      width: 110,
      valueFormatter: (params) =>
        params.value ? moment(params.value).format('MM/DD/YYYY') : '-',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Button
          variant="contained"
          size="small"
          color="success"
          onClick={(e) => {
            e.stopPropagation();
            handleOpenCompleteDialog(params.row);
          }}
        >
          Mark Complete
        </Button>
      ),
    },
  ];

  const syncs = data?.adminOpentableSyncs?.nodes || [];
  const totalCount = data?.adminOpentableSyncs?.totalCount || 0;

  return (
    <>
      <FullScreen paddingBottom={24}>
        {({ height }) => (
          <Paper sx={{ height, width: '100%', p: 2 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
              }}
            >
              <Typography variant="h5">
                OpenTable Sync Dashboard ({totalCount} pending)
              </Typography>
              <TextField
                label="Limit"
                type="number"
                size="small"
                value={limitInput}
                onChange={(e) => setLimitInput(Number(e.target.value) || 100)}
                inputProps={{ min: 1, max: 1000 }}
                sx={{ width: 100 }}
              />
            </Box>
            <Tabs
              value={statusFilter}
              onChange={(_, newValue) => setStatusFilter(newValue)}
              sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab label="All" value="all" />
              <Tab label="Pending" value="pending" />
              <Tab label="Syncing" value="syncing" />
              <Tab label="Failed" value="failed" />
            </Tabs>

            <DataGridPro
              rows={syncs}
              columns={columns}
              loading={loading}
              disableRowSelectionOnClick
              onRowClick={handleRowClick}
              sx={{
                height: 'calc(100% - 105px)',
                ...reportSx,
                '& .MuiDataGrid-row': { cursor: 'pointer' },
              }}
              initialState={{
                sorting: {
                  sortModel: [{ field: 'createdAt', sort: 'asc' }],
                },
              }}
            />
          </Paper>
        )}
      </FullScreen>

      <Dialog
        open={completeDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Mark Sync Complete</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {selectedSync?.opentableCustomInstructions && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, mb: 0.5 }}
                >
                  Custom Instructions
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {selectedSync.opentableCustomInstructions}
                </Typography>
              </Alert>
            )}
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Event:</strong> {selectedSync?.eventName} (ID:{' '}
              {selectedSync?.eventId})
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Venue:</strong> {selectedSync?.venueName}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Guest:</strong> {selectedSync?.guestName} (
              {selectedSync?.guestEmail})
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Date:</strong>{' '}
              {selectedSync?.startDate
                ? moment(selectedSync.startDate).format('MM/DD/YYYY')
                : '-'}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Time:</strong>{' '}
              {selectedSync?.startOffset != null
                ? offsetToTimeString(selectedSync.startOffset)
                : '-'}{' '}
              -{' '}
              {selectedSync?.endOffset != null
                ? offsetToTimeString(selectedSync.endOffset)
                : '-'}
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              <strong>Group Size:</strong> {selectedSync?.groupSize || '-'}
            </Typography>
            <TextField
              fullWidth
              label="OpenTable Reservation ID (optional)"
              value={reservationId}
              onChange={(e) => setReservationId(e.target.value)}
              placeholder="Enter the reservation ID from OpenTable"
              helperText="Optional: Enter the reservation ID after creating it in OpenTable"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleMarkComplete}
            variant="contained"
            color="primary"
            disabled={markingComplete}
          >
            {markingComplete ? 'Marking...' : 'Mark Complete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Detail Modal */}
      <Dialog
        open={detailDialogOpen}
        onClose={handleCloseDetailDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <span>Sync Details</span>
            <Chip
              label={selectedSync?.status}
              size="small"
              color={
                selectedSync?.status === 'pending'
                  ? 'warning'
                  : selectedSync?.status === 'syncing'
                  ? 'info'
                  : 'error'
              }
            />
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            {/* Custom Instructions */}
            {selectedSync?.opentableCustomInstructions && (
              <>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, mb: 0.5 }}
                  >
                    Custom Instructions
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {selectedSync.opentableCustomInstructions}
                  </Typography>
                </Alert>
              </>
            )}

            {/* Venue Section */}
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 1 }}
            >
              Venue
            </Typography>
            <DetailRow
              label="Venue Name"
              value={selectedSync?.venueName}
              onCopy={handleCopy}
              copiedField={copiedField}
              fieldKey="detail-venue"
            />
            <DetailRow
              label="Venue ID"
              value={selectedSync?.venueId}
              onCopy={handleCopy}
              copiedField={copiedField}
              fieldKey="detail-venueId"
            />
            <DetailRow
              label="Restaurant ID"
              value={selectedSync?.opentableRestaurantId}
              onCopy={handleCopy}
              copiedField={copiedField}
              fieldKey="detail-restaurantId"
            />
            <DetailRow
              label="Spaces"
              value={selectedSync?.spaceNames}
              onCopy={handleCopy}
              copiedField={copiedField}
              fieldKey="detail-spaces"
            />

            <Divider sx={{ my: 2 }} />

            {/* Event Section */}
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 1 }}
            >
              Event
            </Typography>
            <DetailRow
              label="Event ID"
              value={selectedSync?.eventId}
              onCopy={handleCopy}
              copiedField={copiedField}
              fieldKey="detail-eventId"
              link={`${APP_HOST}/events/${selectedSync?.eventId}`}
            />
            <DetailRow
              label="Event Name"
              value={selectedSync?.eventName}
              onCopy={handleCopy}
              copiedField={copiedField}
              fieldKey="detail-eventName"
            />
            <DetailRow
              label="Visit Notes"
              value={
                selectedSync
                  ? `Event Name: ${selectedSync.eventName || ''}, Event spaces: ${selectedSync.spaceNames || ''}`
                  : null
              }
              onCopy={handleCopy}
              copiedField={copiedField}
              fieldKey="detail-visitNotes"
            />
            <DetailRow
              label="Event Status"
              value={selectedSync?.eventStatus}
              onCopy={handleCopy}
              copiedField={copiedField}
              fieldKey="detail-eventStatus"
            />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <DetailRow
                  label="Start Date"
                  value={
                    selectedSync?.startDate
                      ? moment(selectedSync.startDate).format('MM/DD/YYYY')
                      : null
                  }
                  onCopy={handleCopy}
                  copiedField={copiedField}
                  fieldKey="detail-startDate"
                />
              </Grid>
              <Grid item xs={6}>
                <DetailRow
                  label="End Date"
                  value={
                    selectedSync?.endDate
                      ? moment(selectedSync.endDate).format('MM/DD/YYYY')
                      : null
                  }
                  onCopy={handleCopy}
                  copiedField={copiedField}
                  fieldKey="detail-endDate"
                />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <DetailRow
                  label="Start Time"
                  value={
                    selectedSync?.startOffset != null
                      ? offsetToTimeString(selectedSync.startOffset)
                      : null
                  }
                  onCopy={handleCopy}
                  copiedField={copiedField}
                  fieldKey="detail-startTime"
                />
              </Grid>
              <Grid item xs={6}>
                <DetailRow
                  label="End Time"
                  value={
                    selectedSync?.endOffset != null
                      ? offsetToTimeString(selectedSync.endOffset)
                      : null
                  }
                  onCopy={handleCopy}
                  copiedField={copiedField}
                  fieldKey="detail-endTime"
                />
              </Grid>
            </Grid>
            <DetailRow
              label="Group Size"
              value={selectedSync?.groupSize?.toString()}
              onCopy={handleCopy}
              copiedField={copiedField}
              fieldKey="detail-groupSize"
            />

            <Divider sx={{ my: 2 }} />

            {/* Guest Section */}
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 1 }}
            >
              Guest
            </Typography>
            <DetailRow
              label="Guest Name"
              value={selectedSync?.guestName}
              onCopy={handleCopy}
              copiedField={copiedField}
              fieldKey="detail-guestName"
            />
            <DetailRow
              label="Guest Email"
              value={selectedSync?.guestEmail}
              onCopy={handleCopy}
              copiedField={copiedField}
              fieldKey="detail-guestEmail"
            />
            <DetailRow
              label="Guest Phone"
              value={formatPhone(selectedSync?.guestPhone || null)}
              onCopy={handleCopy}
              copiedField={copiedField}
              fieldKey="detail-guestPhone"
            />

            <Divider sx={{ my: 2 }} />

            {/* Sync Info Section */}
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 1 }}
            >
              Sync Info
            </Typography>
            <DetailRow
              label="Created"
              value={
                selectedSync?.createdAt
                  ? moment(selectedSync.createdAt).format('MM/DD/YYYY h:mm A')
                  : null
              }
              onCopy={handleCopy}
              copiedField={copiedField}
              fieldKey="detail-created"
            />
            <DetailRow
              label="Updated"
              value={
                selectedSync?.updatedAt
                  ? moment(selectedSync.updatedAt).format('MM/DD/YYYY h:mm A')
                  : null
              }
              onCopy={handleCopy}
              copiedField={copiedField}
              fieldKey="detail-updated"
            />
            {selectedSync?.errorMessage && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Error Message
                </Typography>
                <Typography
                  variant="body2"
                  color="error"
                  sx={{
                    mt: 0.5,
                    p: 1,
                    bgcolor: 'error.light',
                    borderRadius: 1,
                    color: 'error.contrastText',
                  }}
                >
                  {selectedSync.errorMessage}
                </Typography>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Mark Complete Section */}
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 1 }}
            >
              Mark as Complete
            </Typography>
            <TextField
              fullWidth
              size="small"
              label="OpenTable Reservation ID (optional)"
              value={detailReservationId}
              onChange={(e) => setDetailReservationId(e.target.value)}
              placeholder="Enter the reservation ID from OpenTable"
              helperText="Enter the reservation ID after creating it in OpenTable"
              sx={{ mt: 1 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetailDialog}>Close</Button>
          <Button
            onClick={handleMarkCompleteFromDetail}
            variant="contained"
            color="success"
            disabled={markingComplete}
          >
            {markingComplete ? 'Marking...' : 'Mark Complete'}
          </Button>
        </DialogActions>
      </Dialog>

    </>
  );
};

interface DetailRowProps {
  label: string;
  value: string | null | undefined;
  onCopy: (text: string, fieldName: string) => void;
  copiedField: string | null;
  fieldKey: string;
  link?: string;
}

const DetailRow = ({
  label,
  value,
  onCopy,
  copiedField,
  fieldKey,
  link,
}: DetailRowProps) => {
  const displayValue = value || '-';
  const canCopy = !!value;

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        py: 0.75,
      }}
    >
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {displayValue}
        </Typography>
        {link && value && (
          <Tooltip title="Open in Core">
            <IconButton size="small" href={link} target="_blank">
              <ExternalLink size={14} />
            </IconButton>
          </Tooltip>
        )}
        {canCopy && (
          <Tooltip title={copiedField === fieldKey ? 'Copied!' : 'Copy'}>
            <IconButton
              size="small"
              onClick={() => onCopy(value, fieldKey)}
              sx={{ ml: 0.5 }}
            >
              {copiedField === fieldKey ? (
                <Check size={14} />
              ) : (
                <Copy size={14} />
              )}
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

export default OpenTableSyncsTable;
